/**
 * O desenho — `recharts` sobre os pontos que `dados.ts` produziu.
 *
 * `recharts` ^2.15.4 já é dependência de `packages/frontend/core` (`package.json:88`) e já
 * desenha em `detail-page/tabs/analytics.tsx`. Não se traz biblioteca nova para isto.
 *
 * ## Tema (T3.5)
 *
 * Eixos, grade e legenda usam **variáveis de tema**, nunca cor fixa. Um gráfico com eixo
 * `#333` fica invisível no tema escuro — e esse é o defeito que só aparece depois, quando
 * alguém troca o tema. As cores das SÉRIES são fixas de propósito: elas carregam significado
 * (receita, despesa, saldo) e precisam ser as mesmas em todos os oito documentos da F4.
 */

import { cssVarV2 } from '@toeverything/theme/v2';
import { useMemo } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  agregar, corDaSerie, ehCircular, type Agregacao, type LinhaDeDados, type Ponto,
  type Serie, type TipoDeGrafico,
} from './dados';

export interface PropsDoGrafico {
  linhas: LinhaDeDados[];
  tipo: TipoDeGrafico;
  eixoX: string;
  series: Serie[];
  agregacao: Agregacao;
  altura: number;
  /** Como um número vira texto no eixo e no tooltip — vem da coluna (moeda, percentual…). */
  formatarValor?: (valor: number) => string;
}

const eixo = () => ({
  stroke: cssVarV2('text/secondary'),
  fontSize: 12,
});

const grade = () => ({
  stroke: cssVarV2('layer/insideBorder/border'),
  strokeDasharray: '3 3',
  vertical: false,
});

const tooltip = (formatar?: (v: number) => string) => ({
  contentStyle: {
    background: cssVarV2('layer/background/overlayPanel'),
    border: `1px solid ${cssVarV2('layer/insideBorder/border')}`,
    borderRadius: '8px',
    color: cssVarV2('text/primary'),
    fontSize: '13px',
  },
  formatter: (valor: unknown, nome: unknown) => [
    typeof valor === 'number' && formatar ? formatar(valor) : String(valor ?? ''),
    String(nome ?? ''),
  ],
});

export function Grafico(props: PropsDoGrafico) {
  const { tipo, series, agregacao, eixoX, altura, formatarValor } = props;

  const pontos = useMemo(
    () => agregar(props.linhas, { eixoX, series, agregacao }),
    [props.linhas, eixoX, series, agregacao]
  );

  return (
    <ResponsiveContainer width="100%" height={altura}>
      {desenhar(tipo, pontos, series, formatarValor)}
    </ResponsiveContainer>
  );
}

function desenhar(
  tipo: TipoDeGrafico,
  pontos: Ponto[],
  series: Serie[],
  formatar?: (v: number) => string
) {
  const comuns = (
    <>
      <CartesianGrid {...grade()} />
      <XAxis dataKey="categoria" {...eixo()} />
      <YAxis {...eixo()} tickFormatter={formatar ? v => formatar(Number(v)) : undefined} />
      <Tooltip {...tooltip(formatar)} />
      {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
    </>
  );

  if (ehCircular(tipo)) {
    const serie = series[0];
    return (
      <PieChart>
        <Tooltip {...tooltip(formatar)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Pie
          data={pontos}
          dataKey={serie.propertyId}
          nameKey="categoria"
          // A rosca é a pizza com buraco. Um tipo, duas aparências — separar em dois
          // componentes duplicaria tudo para mudar um número.
          innerRadius={tipo === 'rosca' ? '55%' : 0}
          outerRadius="80%"
          paddingAngle={1}
        >
          {pontos.map((p, i) => (
            <Cell key={p.categoria} fill={corDaSerie(serie, i)} />
          ))}
        </Pie>
      </PieChart>
    );
  }

  switch (tipo) {
    case 'linha':
      return (
        <LineChart data={pontos}>
          {comuns}
          {series.map((s, i) => (
            <Line
              key={s.propertyId}
              type="monotone"
              dataKey={s.propertyId}
              name={s.rotulo}
              stroke={corDaSerie(s, i)}
              strokeWidth={2}
              dot={false}
              // ⚠️ `connectNulls={false}`: um mês sem dado deve aparecer como BURACO. Ligando
              // os pontos por cima da ausência, a linha inventa uma tendência que não existe
              // — e num gráfico de saldo isso é a diferença entre "não lancei" e "foi zero".
              connectNulls={false}
            />
          ))}
        </LineChart>
      );

    case 'area':
      return (
        <AreaChart data={pontos}>
          {comuns}
          {series.map((s, i) => (
            <Area
              key={s.propertyId}
              type="monotone"
              dataKey={s.propertyId}
              name={s.rotulo}
              stroke={corDaSerie(s, i)}
              fill={corDaSerie(s, i)}
              fillOpacity={0.18}
              strokeWidth={2}
              connectNulls={false}
            />
          ))}
        </AreaChart>
      );

    case 'combo':
      return (
        <ComposedChart data={pontos}>
          {comuns}
          {series.map((s, i) =>
            // O combo existe para "realizado × meta": o realizado é barra, a meta é linha.
            // O default é barra porque, com uma série só, barra é o que se espera ver.
            s.forma === 'linha' ? (
              <Line
                key={s.propertyId}
                type="monotone"
                dataKey={s.propertyId}
                name={s.rotulo}
                stroke={corDaSerie(s, i)}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            ) : (
              <Bar
                key={s.propertyId}
                dataKey={s.propertyId}
                name={s.rotulo}
                fill={corDaSerie(s, i)}
                radius={[3, 3, 0, 0]}
              />
            )
          )}
        </ComposedChart>
      );

    case 'barra-empilhada':
    case 'barra':
    default:
      return (
        <BarChart data={pontos}>
          {comuns}
          {series.map((s, i) => (
            <Bar
              key={s.propertyId}
              dataKey={s.propertyId}
              name={s.rotulo}
              fill={corDaSerie(s, i)}
              // O `stackId` compartilhado é o que empilha. Igual para todas as séries no
              // empilhado; ausente no agrupado.
              stackId={tipo === 'barra-empilhada' ? 'pilha' : undefined}
              radius={tipo === 'barra-empilhada' ? undefined : [3, 3, 0, 0]}
            />
          ))}
        </BarChart>
      );
  }
}
